"use client";

/**
 * AdminLayout — 관리자 페이지 메뉴 + 헤더 레이아웃
 *
 * `type` prop 으로 데스크탑/모바일 레이아웃을 명시적으로 지정한다.
 * - `"desktop"` : 좌측 고정 사이드바 + 상단 헤더. 사이드바 접기/펼치기 가능.
 * - `"mobile"`  : 사이드바 없이 상단 헤더 + 햄버거 Drawer 메뉴.
 * - 미지정      : useIsMobile() 결과에 따라 자동 전환 (반응형).
 *
 * 페이지 전체 모바일 너비 제한이 필요하면 루트 layout.tsx 에서
 * MobileContainer 로 감싼다. (AdminLayout 과 별개로 동작)
 *
 * @example
 * // 데스크탑 관리자
 * <AdminLayout type="desktop" nav={nav} pathname={pathname} LinkComponent={Link}>
 *   {children}
 * </AdminLayout>
 *
 * @example
 * // 모바일 웹뷰 관리자 (루트에 MobileContainer 적용 후)
 * <AdminLayout type="mobile" nav={nav} pathname={pathname} LinkComponent={Link}>
 *   {children}
 * </AdminLayout>
 */
import { type ReactNode, useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import type {
  AdminUser,
  HeaderTheme,
  LinkComponent,
  NavGroup,
  NavItem,
  SidebarTheme,
} from "../../types";
import { useIsMobile } from "../../hooks/use-is-mobile";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { TooltipProvider } from "../ui/tooltip";
import { AdminHeader } from "./admin-header";
import { AdminLayoutProvider } from "./admin-layout-context";
import { AdminSidebar, sidebarThemeToStyle } from "./admin-sidebar";

export interface AdminLayoutProps {
  /**
   * 레이아웃 모드.
   * - `"desktop"` : 사이드바 + 헤더 (PC 관리자 페이지)
   * - `"mobile"`  : 헤더 + 햄버거 Drawer (모바일 웹뷰)
   * - 미지정      : 뷰포트 너비에 따라 자동 전환 (반응형)
   */
  type?: "desktop" | "mobile";
  /** 사이드 메뉴 구성 */
  nav: NavItem[] | NavGroup[];
  /** 현재 활성 경로 (Next.js: usePathname()) */
  pathname?: string;
  /** 라우팅 링크 컴포넌트 (Next.js: next/link 의 Link, 기본: <a>) */
  LinkComponent?: LinkComponent;
  /**
   * 사이드바 상단 로고/브랜드 영역.
   * 함수를 넘기면 접힘 상태(collapsed)를 받아 다르게 렌더링할 수 있다.
   */
  logo?: ReactNode | ((collapsed: boolean) => ReactNode);
  /** 사이드바 하단 고정 영역 */
  sidebarFooter?: ReactNode;
  /** 사이드바 색상 테마 */
  sidebarTheme?: SidebarTheme;

  /** 헤더 타이틀 */
  title?: ReactNode;
  /** 로그인 사용자 정보 */
  user?: AdminUser;
  /** 헤더 우측 추가 액션 */
  headerActions?: ReactNode;
  /** 로그아웃 콜백 */
  onLogout?: () => void;
  /** 사용자 드롭다운 커스텀 항목 */
  userMenuItems?: ReactNode;
  /** 기본 "프로필" 메뉴 클릭 시 이동할 경로 */
  profileHref?: string;
  /** 헤더 색상 테마 */
  headerTheme?: HeaderTheme;
  /** 기본 AdminHeader 대신 완전히 커스텀한 헤더를 쓸 때 */
  header?: ReactNode;

  /** 초기 사이드바 접힘 상태 (데스크탑 전용) */
  defaultCollapsed?: boolean;
  /** 본문 영역 컨테이너 클래스 */
  contentClassName?: string;
  children: ReactNode;
}

export function AdminLayout({
  type,
  nav,
  pathname,
  LinkComponent,
  logo,
  sidebarFooter,
  sidebarTheme,
  title,
  user,
  headerActions,
  onLogout,
  userMenuItems,
  profileHref,
  headerTheme,
  header,
  defaultCollapsed = false,
  contentClassName,
  children,
}: AdminLayoutProps) {
  const isMobileViewport = useIsMobile();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  // type prop 으로 강제 지정하거나 뷰포트로 자동 결정
  const isMobile =
    type === "mobile" ? true : type === "desktop" ? false : isMobileViewport;

  const ctxValue = useMemo(
    () => ({
      collapsed,
      setCollapsed,
      toggleCollapsed: () => setCollapsed((v) => !v),
      mobileOpen,
      setMobileOpen,
      isMobile,
    }),
    [collapsed, mobileOpen, isMobile]
  );

  return (
    <TooltipProvider>
      <AdminLayoutProvider value={ctxValue}>
        <div className="flex min-h-screen w-full bg-background">
          {/* 데스크탑 사이드바 */}
          {!isMobile && (
            <aside
              data-collapsed={collapsed}
              style={sidebarThemeToStyle(sidebarTheme)}
              className={cn(
                "sticky top-0 h-screen shrink-0 border-r border-sidebar-border transition-[width] duration-200",
                collapsed ? "w-16" : "w-64"
              )}
            >
              <AdminSidebar
                nav={nav}
                pathname={pathname}
                LinkComponent={LinkComponent}
                logo={logo}
                footer={collapsed ? undefined : sidebarFooter}
                collapsed={collapsed}
                theme={sidebarTheme}
              />
            </aside>
          )}

          {/* 모바일 Drawer — isMobile 일 때만 렌더 */}
          <Sheet open={isMobile && mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className="w-72 p-0">
              <SheetTitle className="sr-only">사이드 메뉴</SheetTitle>
              <AdminSidebar
                nav={nav}
                pathname={pathname}
                LinkComponent={LinkComponent}
                logo={logo}
                footer={sidebarFooter}
                theme={sidebarTheme}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>

          {/* 본문 컬럼 */}
          <div className="flex min-w-0 flex-1 flex-col">
            {header ?? (
              <AdminHeader
                title={title}
                user={user}
                actions={headerActions}
                onLogout={onLogout}
                userMenuItems={userMenuItems}
                profileHref={profileHref}
                LinkComponent={LinkComponent}
                theme={headerTheme}
              />
            )}
            <main
              className={cn(
                "flex-1 bg-muted/40 px-4 py-4 sm:px-8 sm:py-6",
                contentClassName
              )}
            >
              {children}
            </main>
          </div>
        </div>
      </AdminLayoutProvider>
    </TooltipProvider>
  );
}
